import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Lock, Download, X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import JustifiedGallery from "@/components/JustifiedGallery";

interface Photo {
  id: string;
  thumbnail_url: string;
  full_url: string;
  width: number;
  height: number;
}

const GalleryView = () => {
  const { slug } = useParams<{ slug: string }>();
  const [password, setPassword] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [galleryTitle, setGalleryTitle] = useState("");
  const [eventDate, setEventDate] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [allowDownloads, setAllowDownloads] = useState(true);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  const functionUrl = (name: string, query = "") => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID as string | undefined;
    const baseUrl = supabaseUrl || (projectId ? `https://${projectId}.supabase.co` : "");
    return `${baseUrl}/functions/v1/${name}${query}`;
  };

  const requestJson = <T,>(url: string, options: { method?: string; headers?: Record<string, string>; body?: unknown } = {}) =>
    new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || "GET", url, true);
      xhr.responseType = "text";
      xhr.timeout = 30000;
      Object.entries(options.headers || {}).forEach(([key, value]) => xhr.setRequestHeader(key, value));
      xhr.onload = () => {
        const data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
        if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
        else reject(new Error(data.error || "Erreur de chargement"));
      };
      xhr.onerror = () => reject(new Error("Impossible de joindre la galerie. Réessayez dans un instant."));
      xhr.ontimeout = () => reject(new Error("La demande a pris trop de temps. Réessayez."));
      xhr.send(options.body ? JSON.stringify(options.body) : undefined);
    });

  useEffect(() => {
    if (!slug) return;
    const stored = sessionStorage.getItem(`gallery_token_${slug}`);
    if (stored) {
      setToken(stored);
      return;
    }
    // Try public access (no password)
    (async () => {
      try {
        const data = await requestJson<{ token: string; gallery: { title: string } }>(
          functionUrl("gallery-access"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            },
            body: { slug, password: "" },
          },
        );
        sessionStorage.setItem(`gallery_token_${slug}`, data.token);
        setToken(data.token);
        setGalleryTitle(data.gallery.title);
      } catch {
        // Not public — show password form
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!token || !slug) return;
    const load = async () => {
      setLoading(true);
      try {
        const data = await requestJson<{
          photos: Photo[];
          allow_downloads: boolean;
          gallery?: { title: string; event_date: string | null; cover_image_url: string | null };
        }>(
          functionUrl("gallery-photos", `?slug=${encodeURIComponent(slug)}`),
          {
            headers: {
              "x-gallery-token": token,
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            },
          },
        );
        setPhotos(data.photos);
        setAllowDownloads(data.allow_downloads);
        if (data.gallery) {
          setGalleryTitle(data.gallery.title);
          setEventDate(data.gallery.event_date);
          setCoverUrl(data.gallery.cover_image_url);
        }
      } catch (e: any) {
        toast.error(e.message || "Erreur de chargement");
        sessionStorage.removeItem(`gallery_token_${slug}`);
        setToken(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, slug]);

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug) return;
    setUnlocking(true);
    try {
      const data = await requestJson<{ token: string; gallery: { title: string } }>(functionUrl("gallery-access"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: { slug, password },
      });
      sessionStorage.setItem(`gallery_token_${slug}`, data.token);
      setToken(data.token);
      setGalleryTitle(data.gallery.title);
    } catch (e: any) {
      toast.error(e.message || "Mot de passe incorrect");
    } finally {
      setUnlocking(false);
    }
  };

  // Navigation handlers
  const goPrev = useCallback(() => {
    setLightboxIdx((i) => (i === null ? null : Math.max(0, i - 1)));
  }, []);
  const goNext = useCallback(() => {
    setLightboxIdx((i) => (i === null || i >= photos.length - 1 ? i : i + 1));
  }, [photos.length]);

  // Keyboard nav for lightbox
  useEffect(() => {
    if (lightboxIdx === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxIdx(null);
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxIdx, goPrev, goNext]);

  // Preload neighbours for instant navigation
  useEffect(() => {
    if (lightboxIdx === null) return;
    const toPreload = [lightboxIdx - 1, lightboxIdx + 1, lightboxIdx + 2, lightboxIdx - 2];
    toPreload.forEach((idx) => {
      if (idx >= 0 && idx < photos.length) {
        const img = new Image();
        img.src = photos[idx].full_url;
      }
    });
  }, [lightboxIdx, photos]);

  if (!token) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center p-4">
        <meta name="robots" content="noindex,nofollow" />
        <Card className="max-w-md w-full p-8">
          <div className="text-center mb-6">
            <div className="inline-flex p-3 rounded-full bg-primary/10 mb-4">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Galerie privée</h1>
            <p className="text-muted-foreground text-sm">
              Entrez le mot de passe qui vous a été remis pour accéder aux photos.
            </p>
          </div>
          <form onSubmit={handleUnlock} className="space-y-4">
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={unlocking}>
              {unlocking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Déverrouiller"}
            </Button>
          </form>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background">
      <meta name="robots" content="noindex,nofollow" />
      <nav className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-20">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <a
            href="https://agence8020.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm md:text-base font-semibold tracking-tight hover:text-primary transition-colors"
          >
            Agence 8020
          </a>
          <div className="flex items-center gap-3 md:gap-5 text-sm">
            <a
              href="https://agence8020.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors hidden sm:inline"
            >
              Découvrir l'agence
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center rounded-full bg-primary text-primary-foreground px-3 md:px-4 py-1.5 hover:opacity-90 transition-opacity"
            >
              Nous contacter
            </a>
          </div>
        </div>
      </nav>




      {coverUrl ? (
        <header className="relative w-full h-[55vh] md:h-[75vh] min-h-[360px] overflow-hidden bg-muted">
          <img
            src={coverUrl}
            alt={galleryTitle}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/70" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1
              className="text-white text-4xl md:text-7xl font-light tracking-tight drop-shadow-lg"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              {galleryTitle || "Galerie"}
            </h1>
            {eventDate && (
              <p className="mt-3 text-white/90 text-sm md:text-base uppercase tracking-[0.25em]">
                {new Date(eventDate).toLocaleDateString("fr-CA", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            )}
            <p className="mt-4 text-white/80 text-xs md:text-sm">
              {photos.length} photo{photos.length > 1 ? "s" : ""}
            </p>
          </div>
        </header>
      ) : (
        <header className="border-b border-border py-10 md:py-16 px-4 text-center">
          <h1
            className="text-3xl md:text-5xl font-light"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            {galleryTitle || "Galerie"}
          </h1>
          {eventDate && (
            <p className="mt-3 text-muted-foreground text-xs md:text-sm uppercase tracking-[0.25em]">
              {new Date(eventDate).toLocaleDateString("fr-CA", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          )}
          <p className="mt-3 text-sm text-muted-foreground">
            {photos.length} photo{photos.length > 1 ? "s" : ""}
          </p>
        </header>
      )}

      <div className="max-w-[1600px] mx-auto px-2 md:px-6 py-6 md:py-10">
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          </div>
        ) : (
          <JustifiedGallery
            photos={photos}
            onSelect={(i) => setLightboxIdx(i)}
          />
        )}
      </div>

      {lightboxIdx !== null && photos[lightboxIdx] && (
      <div
        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center touch-none select-none"
        onClick={() => setLightboxIdx(null)}
        onTouchStart={(e) => {
          (e.currentTarget as any)._touchX = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          const startX = (e.currentTarget as any)._touchX;
          if (startX == null) return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 50) {
            if (dx < 0) goNext();
            if (dx > 0) goPrev();
          }
        }}
      >
        <button
          onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
          className="absolute top-3 right-3 md:top-4 md:right-4 text-white p-2 bg-black/40 hover:bg-white/20 rounded-full z-20"
          aria-label="Fermer"
        >
          <X className="h-5 w-5 md:h-6 md:w-6" />
        </button>

        <div className="absolute top-3 left-3 md:top-4 md:left-4 text-white text-sm bg-black/40 px-3 py-1 rounded-full z-20">
          {lightboxIdx + 1} / {photos.length}
        </div>

        {lightboxIdx > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 text-white p-2 md:p-3 bg-black/40 hover:bg-white/20 rounded-full z-20"
            aria-label="Précédent"
          >
            <ChevronLeft className="h-6 w-6 md:h-8 md:w-8" />
          </button>
        )}
        {lightboxIdx < photos.length - 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 text-white p-2 md:p-3 bg-black/40 hover:bg-white/20 rounded-full z-20"
            aria-label="Suivant"
          >
            <ChevronRight className="h-6 w-6 md:h-8 md:w-8" />
          </button>
        )}

        {/* Render only current + neighbours, keep them mounted to leverage browser cache */}
        <div className="relative w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          {photos.map((p, i) => {
            const distance = Math.abs(i - lightboxIdx);
            if (distance > 2) return null;
            const isActive = i === lightboxIdx;
            return (
              <img
                key={p.id}
                src={p.full_url}
                alt=""
                draggable={false}
                className={`absolute max-h-[85vh] max-w-[95vw] md:max-h-[90vh] md:max-w-[90vw] object-contain transition-opacity duration-150 ${isActive ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              />
            );
          })}
        </div>

        {allowDownloads && (
          <a
            href={photos[lightboxIdx].full_url}
            download
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 bg-white text-black px-4 py-2 rounded-full flex items-center gap-2 hover:bg-white/90 text-sm md:text-base shadow-lg z-20"
          >
            <Download className="h-4 w-4" /> Télécharger
          </a>
        )}
      </div>
    )}
  </main>
);
};

export default GalleryView;
